import mixpanel from 'mixpanel-browser';

import { Singleton } from '../../common/utils/Singleton';
import { MIXPANEL_TOKEN } from '../../config/config';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import type { AdminUser } from '../../entities/entityTypes';
import type { TrackedEvent } from '../Events';
import type { IMixpanel } from './types/Mixpanel';

const isMixpanelEnabled = () => {
    return !IS_LOCAL && MIXPANEL_TOKEN != null;
};

class MixpanelFacade extends Singleton implements IMixpanel {
    public readonly Mixpanel: typeof mixpanel;

    constructor() {
        super();
        this.Mixpanel = mixpanel;
    }

    public init(): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        this.Mixpanel.init(MIXPANEL_TOKEN as string, {
            debug: IS_LOCAL,
            track_pageview: true,
            persistence: 'localStorage',
            ignore_dnt: true,
        });
    }

    public identifyUser(user: AdminUser): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        this.Mixpanel.identify(user.id ?? '');
        this.Mixpanel.people.set({
            $email: user.email,
        }); // people.set will update existing data.
    }

    public track(
        event: TrackedEvent,
        data?: Record<string, string | number | boolean>,
        sendImmediately?: boolean,
    ): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        if (sendImmediately) {
            this.Mixpanel.track(event, data, {
                send_immediately: true,
            });
        } else {
            this.Mixpanel.track(event, data);
        }
    }

    public reset(): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        this.Mixpanel.reset();
    }
}

export default MixpanelFacade.getInstance();
